import React, { useState, useRef, useEffect } from 'react';
import { createPortal } from 'react-dom';
import IconSystem from 'icon-collection/icon-systems';

/**
 * ============================================================================
 * MODULE HEADER COMPONENT - REUSABLE HEADER FOR ALL MASTER SCREENS
 * ============================================================================
 *
 * A flexible, configurable header component that can be used across all
 * master screens (Service Master, GL Master, Customer Master, etc.)
 *
 * FEATURES:
 * ✅ Configurable title and description
 * ✅ Optional search functionality
 * ✅ Flexible view mode toggles (list, grid, calendar, etc.)
 * ✅ Optional filter button
 * ✅ Custom toolbar items slot (for Column Selector, etc.)
 * ✅ Configurable action buttons (Add, Refresh, Export, Delete, etc.)
 * ✅ Type-safe with TypeScript
 * ✅ Easy to extend without modifying base component
 * ✅ Responsive overflow menu for smaller screens
 * ============================================================================
 */

// ============================================================================
// TYPE DEFINITIONS
// ============================================================================

/**
 * Configuration for an individual action button
 */
export interface ActionButton {
  id: string;
  icon: React.ReactNode;
  label: string;
  onClick: () => void;
  tooltip?: string;
  disabled?: boolean;
  variant?: 'primary' | 'secondary' | 'icon' | 'danger';
  visible?: boolean; // Allow buttons to be hidden conditionally (default: true)
}

/**
 * Configuration for view mode options (list, grid, calendar, etc.)
 */
export interface ViewModeOption {
  id: string;
  icon: React.ReactNode;
  label: string;
  tooltip?: string;
  disabled?: boolean;
}

/**
 * Configuration for the search bar
 */
export interface SearchConfig {
  value: string;
  onChange: (value: string) => void;
  placeholder?: string;
  disabled?: boolean;
}

/**
 * Configuration for view mode toggle
 */
export interface ViewModeConfig {
  currentMode: string;
  modes: ViewModeOption[];
  onChange: (mode: string) => void;
}

/**
 * Configuration for the filter button
 */
export interface FilterConfig {
  onClick: () => void;
  isActive?: boolean;
  disabled?: boolean;
  tooltip?: string;
  /** When > 0, a small badge shows the number of applied filters over the funnel icon. */
  appliedCount?: number;
}

/**
 * Main props for the ModuleHeader component
 */
export interface ModuleHeaderProps {
  // Title section (required)
  title: string;
  description?: string;
  icon?: React.ReactNode;

  // Search (optional)
  searchConfig?: SearchConfig;

  // View modes (optional)
  viewModeConfig?: ViewModeConfig;

  // Filter (optional)
  filterConfig?: FilterConfig;

  // Leading toolbar items (optional) - rendered before view modes (e.g., MinimizedModalStrip)
  leadingToolbarItems?: React.ReactNode;

  // Custom toolbar items (optional) - for components like ColumnSelector
  customToolbarItems?: React.ReactNode;

  // Action buttons (optional)
  actions?: ActionButton[];

  // Show separator before actions (default: true)
  showActionSeparator?: boolean;

  // Control visibility of entire header component (default: true)
  visible?: boolean;

  // Custom className for styling
  className?: string;
}

// ============================================================================
// MODULE HEADER COMPONENT
// ============================================================================

const ModuleHeader: React.FC<ModuleHeaderProps> = ({
  title,
  description,
  icon,
  searchConfig,
  viewModeConfig,
  filterConfig,
  leadingToolbarItems,
  customToolbarItems,
  actions = [],
  showActionSeparator = true,
  visible = true,
  className = '',
}) => {
  const [overflowMenuOpen, setOverflowMenuOpen] = useState(false);
  const [menuPos, setMenuPos] = useState({ top: 0, right: 0 });
  const overflowMenuRef = useRef<HTMLDivElement>(null);
  const overflowTriggerRef = useRef<HTMLButtonElement>(null);

  // Close overflow menu when clicking outside
  // Note: This hook must be called before any conditional returns
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        overflowMenuRef.current && !overflowMenuRef.current.contains(event.target as Node) &&
        overflowTriggerRef.current && !overflowTriggerRef.current.contains(event.target as Node)
      ) {
        setOverflowMenuOpen(false);
      }
    };

    if (overflowMenuOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [overflowMenuOpen]);

  // If header is not visible, return null
  if (!visible) {
    return null;
  }

  // Filter out invisible actions (default visibility is true)
  const visibleActions = actions.filter(action => action.visible !== false);

  // Only show overflow menu trigger when there's content to show
  const hasOverflowContent = !!(viewModeConfig || filterConfig || customToolbarItems || visibleActions.length > 0);

  return (
    <div className={`module-header ${className}`}>
      {/* Inline styles for responsive overflow menu */}
      <style>{`
        /* Desktop toolbar items - visible on larger screens */
        .toolbar-desktop-items {
          display: flex;
          align-items: center;
          gap: var(--spacing-sm, 0.5rem);
          flex-wrap: wrap;
        }

        /* Mobile overflow menu - hidden on larger screens */
        .toolbar-overflow-menu {
          display: none;
        }

        /* Overflow menu section label */
        .overflow-menu-section-label {
          padding: 0.5rem 0.875rem 0.25rem;
          font-size: 0.7rem;
          font-weight: 600;
          color: var(--text-muted, #94a3b8);
          text-transform: uppercase;
          letter-spacing: 0.5px;
        }

        /* Overflow menu separator */
        .overflow-menu-separator {
          height: 1px;
          background: var(--border-color, #e2e8f0);
          margin: 0.5rem 0;
        }

        /* Active state for overflow menu items */
        .action-menu-item.active {
          background: var(--bg-secondary, #f1f5f9);
          color: var(--primary, #3b82f6);
        }

        /* Danger variant for overflow menu items */
        .action-menu-item.danger {
          color: var(--danger, #ef4444);
        }

        .action-menu-item.danger:hover {
          background: rgba(239, 68, 68, 0.1);
          color: var(--danger, #ef4444);
        }

        /* Responsive: Show overflow menu on mobile, hide desktop items */
        @media (max-width: 768px) {
          .toolbar-desktop-items {
            display: none !important;
          }

          .toolbar-overflow-menu {
            display: block !important;
          }

          .module-header {
            padding: 0.75rem 1rem !important;
            margin: 0.5rem 0.75rem !important;
            gap: 0.5rem !important;
            flex-direction: row !important;
            align-items: center !important;
          }

          .module-header-content {
            flex: 1 !important;
            min-width: 0 !important;
            flex-direction: row !important;
            align-items: center !important;
            justify-content: space-between !important;
          }

          .module-toolbar {
            width: auto !important;
            flex-shrink: 0 !important;
          }

          .module-description {
            display: none !important;
          }

          .module-title {
            font-size: 0.95rem !important;
          }

          .search-input-wrapper {
            min-width: 120px !important;
            flex: 1 !important;
          }

          /* Fix overflow menu position on mobile - opens downward */
          .toolbar-overflow-menu .action-menu-dropdown {
            position: absolute !important;
            top: calc(100% + 0.5rem) !important;
            bottom: auto !important;
            left: auto !important;
            right: 0 !important;
            min-width: 200px !important;
            max-width: calc(100vw - 2rem) !important;
            max-height: 60vh !important;
            border-radius: 0.5rem !important;
            box-shadow: 0 4px 24px rgba(0, 0, 0, 0.2) !important;
            animation: slideDown 0.2s ease !important;
          }

          @keyframes slideDown {
            from {
              opacity: 0;
              transform: translateY(-8px);
            }
            to {
              opacity: 1;
              transform: translateY(0);
            }
          }
        }
      `}</style>

      {/* Title Section */}
      <div className="module-header-content">
        <div style={icon ? { display: 'flex', alignItems: 'center', gap: '0.5rem' } : undefined}>
          {icon && (
            <span style={{ display: 'flex', alignItems: 'center', color: 'var(--primary)', opacity: 0.9, flexShrink: 0 }}>
              {icon}
            </span>
          )}
          <h1 className="module-title">{title}</h1>
        </div>
        {description && <p className="module-description">{description}</p>}
      </div>

      {/* Toolbar Section */}
      <div className="module-toolbar">
        {/* Search Bar */}
        {searchConfig && (
          <div className="search-input-wrapper">
            <IconSystem.search size={16} />
            <input
              type="text"
              className="search-input"
              placeholder={searchConfig.placeholder || 'Search...'}
              value={searchConfig.value}
              onChange={(e) => searchConfig.onChange(e.target.value)}
              disabled={searchConfig.disabled}
            />
          </div>
        )}

        {/* Leading Toolbar Items - always visible (e.g., MinimizedModalStrip) */}
        {leadingToolbarItems}

        {/* Desktop Toolbar Items */}
        <div className="toolbar-desktop-items">
          {/* View Mode Toggle */}
          {viewModeConfig && (
            <div className="view-mode-group">
              {viewModeConfig.modes.map((mode) => (
                <button
                  key={mode.id}
                  className={`view-mode-btn ${viewModeConfig.currentMode === mode.id ? 'active' : ''}`}
                  onClick={() => viewModeConfig.onChange(mode.id)}
                  disabled={mode.disabled}
                  data-tooltip={mode.tooltip}
                  title={mode.tooltip || mode.label}
                  style={mode.disabled ? { opacity: 0.3, cursor: 'not-allowed' } : {}}
                >
                  {mode.icon}
                </button>
              ))}
            </div>
          )}

          {/* Filter Button — with applied-count badge when filterConfig.appliedCount > 0.
              position: relative on the button so the absolutely-positioned badge
              anchors to its top-right corner. */}
          {filterConfig && (
            <button
              className={`btn-icon ${filterConfig.isActive ? 'active' : ''}`}
              onClick={filterConfig.onClick}
              disabled={filterConfig.disabled}
              title={
                filterConfig.tooltip
                  || (filterConfig.appliedCount
                    ? `Filters (${filterConfig.appliedCount} applied)`
                    : 'Filters')
              }
              style={{ position: 'relative' }}
            >
              <IconSystem.filter size={18} />
              {filterConfig.appliedCount && filterConfig.appliedCount > 0 ? (
                <span
                  aria-label={`${filterConfig.appliedCount} applied filters`}
                  style={{
                    position: 'absolute',
                    top: -2,
                    right: -2,
                    minWidth: '16px',
                    height: '16px',
                    padding: '0 4px',
                    borderRadius: '8px',
                    background: '#dc2626',
                    color: '#fff',
                    fontSize: '0.625rem',
                    fontWeight: 700,
                    lineHeight: '16px',
                    textAlign: 'center',
                    boxSizing: 'border-box',
                    boxShadow: '0 0 0 1.5px var(--bg-primary, #fff)',
                  }}
                >
                  {filterConfig.appliedCount > 99 ? '99+' : filterConfig.appliedCount}
                </span>
              ) : null}
            </button>
          )}

          {/* Custom Toolbar Items (e.g., Column Selector) */}
          {customToolbarItems}

          {/* Separator before actions */}
          {showActionSeparator && visibleActions.length > 0 && (
            <div className="toolbar-separator" />
          )}

          {/* Action Buttons */}
          {visibleActions.map((action) => {
            const buttonClass = action.variant === 'primary'
              ? 'btn btn-primary'
              : action.variant === 'secondary'
              ? 'btn btn-secondary'
              : action.variant === 'danger'
              ? 'btn btn-danger'
              : 'btn-icon';

            return (
              <button
                key={action.id}
                className={buttonClass}
                onClick={action.onClick}
                disabled={action.disabled}
                title={action.tooltip || action.label}
              >
                {action.icon}
                {action.variant !== 'icon' && (
                  <span style={{ marginLeft: '0.5rem' }}>{action.label}</span>
                )}
              </button>
            );
          })}
        </div>

        {/* Mobile Overflow Menu — only rendered when there are items to show */}
        {hasOverflowContent && (
        <div className="toolbar-overflow-menu action-menu-container">
          <button
            ref={overflowTriggerRef}
            onClick={() => {
              if (!overflowMenuOpen && overflowTriggerRef.current) {
                const rect = overflowTriggerRef.current.getBoundingClientRect();
                setMenuPos({
                  top: rect.bottom + 4,
                  right: window.innerWidth - rect.right,
                });
              }
              setOverflowMenuOpen(!overflowMenuOpen);
            }}
            className={`action-menu-trigger ${overflowMenuOpen ? 'active' : ''}`}
            title="More options"
          >
            <IconSystem.moreVertical size={18} />
          </button>

          {overflowMenuOpen && createPortal(
            <>
              <div
                onClick={() => setOverflowMenuOpen(false)}
                style={{ position: 'fixed', inset: 0, zIndex: 9998, background: 'transparent' }}
              />
              <div
                ref={overflowMenuRef}
                className="action-menu-dropdown"
                style={{
                  position: 'fixed',
                  top: menuPos.top,
                  right: menuPos.right,
                  zIndex: 9999,
                  minWidth: '200px',
                  maxHeight: '70vh',
                  overflowY: 'auto',
                  border: '2px solid var(--primary)',
                  boxShadow: '0 12px 32px rgba(37, 99, 235, 0.2)',
                }}
              >
              {/* View Mode Options */}
              {viewModeConfig && (
                <>
                  <div className="overflow-menu-section-label">View</div>
                  {viewModeConfig.modes.map((mode) => (
                    <button
                      key={mode.id}
                      className={`action-menu-item ${viewModeConfig.currentMode === mode.id ? 'active' : ''}`}
                      onClick={() => {
                        viewModeConfig.onChange(mode.id);
                        setOverflowMenuOpen(false);
                      }}
                      disabled={mode.disabled}
                    >
                      {mode.icon}
                      <span>{mode.label}</span>
                    </button>
                  ))}
                </>
              )}

              {/* Filter Option (responsive overflow menu). Same appliedCount
                  badge shape as the desktop toolbar button — trailing pill next
                  to the label. */}
              {filterConfig && (
                <button
                  className={`action-menu-item ${filterConfig.isActive ? 'active' : ''}`}
                  onClick={() => {
                    filterConfig.onClick();
                    setOverflowMenuOpen(false);
                  }}
                  disabled={filterConfig.disabled}
                >
                  <IconSystem.filter size={18} />
                  <span>Filters</span>
                  {filterConfig.appliedCount && filterConfig.appliedCount > 0 ? (
                    <span
                      aria-label={`${filterConfig.appliedCount} applied filters`}
                      style={{
                        marginLeft: 'auto',
                        minWidth: '18px',
                        height: '18px',
                        padding: '0 5px',
                        borderRadius: '9px',
                        background: '#dc2626',
                        color: '#fff',
                        fontSize: '0.6875rem',
                        fontWeight: 700,
                        lineHeight: '18px',
                        textAlign: 'center',
                        boxSizing: 'border-box',
                      }}
                    >
                      {filterConfig.appliedCount > 99 ? '99+' : filterConfig.appliedCount}
                    </span>
                  ) : null}
                </button>
              )}

              {/* Custom Toolbar Items Section */}
              {customToolbarItems && (
                <>
                  {(viewModeConfig || filterConfig) && (
                    <div className="overflow-menu-separator" />
                  )}
                  <div className="overflow-menu-section-label">Tools</div>
                  <div style={{ padding: '0.25rem 0.875rem', display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                    {customToolbarItems}
                    <span style={{ fontSize: '0.85rem', color: 'var(--text-primary)' }}>Manage Columns</span>
                  </div>
                </>
              )}

              {/* Separator if we have actions */}
              {visibleActions.length > 0 && (viewModeConfig || filterConfig || customToolbarItems) && (
                <div className="overflow-menu-separator" />
              )}

              {/* Action Buttons */}
              {visibleActions.length > 0 && (
                <>
                  <div className="overflow-menu-section-label">Actions</div>
                  {visibleActions.map((action) => (
                    <button
                      key={action.id}
                      className={`action-menu-item ${action.variant === 'danger' ? 'danger' : ''}`}
                      onClick={() => {
                        action.onClick();
                        setOverflowMenuOpen(false);
                      }}
                      disabled={action.disabled}
                    >
                      {action.icon}
                      <span>{action.label}</span>
                    </button>
                  ))}
                </>
              )}
            </div>
            </>,
            document.body
          )}
        </div>
        )}
      </div>
    </div>
  );
};

export default ModuleHeader;
